// Diagnóstico de la obra: convierte las detecciones en hallazgos con una acción concreta.
import {
  photoStats, periodKpis, dayKey, listDays, fmtKey, fmtDow, fmtMin, fmtHM, isMachine, arMin,
} from './iaData';

const minOfDay = arMin;
const r1 = (v) => Math.round(v * 10) / 10;
const hm = (min) => (min >= 60 ? `${Math.floor(min / 60)} h ${String(Math.round(min % 60)).padStart(2, '0')}` : `${Math.round(min)} min`);
const SLOT = 30;

// Perfil de un día típico (franjas de 30 min) y semana tipo
export function profiles(photos, cfg) {
  const slots = new Map();
  const days = new Map();
  photos.forEach((p, i) => {
    const s = photoStats(p, cfg);
    const m = minOfDay(p.ms), k = Math.floor(m / SLOT) * SLOT;
    const o = slots.get(k) || { min: k, n: 0, persons: 0, act: 0, helmet: 0 };
    o.n += 1; o.persons += s.persons; o.act += s.activity; o.helmet += s.helmet;
    slots.set(k, o);
    const next = photos[i + 1];
    const dt = Math.min(next && dayKey(next.ms) === dayKey(p.ms) ? next.ms - p.ms : 10 * 60e3, 15 * 60e3) / 3600e3;
    const dk = dayKey(p.ms);
    const d = days.get(dk) || { hh: 0, act: 0 };
    d.hh += s.persons * dt; d.act += s.activity;
    days.set(dk, d);
  });
  const day = [...slots.values()].sort((a, b) => a.min - b.min).map((o) => ({
    min: o.min, label: fmtMin(o.min),
    personas: r1(o.persons / o.n), actividad: r1(o.act / o.n),
    casco: o.persons ? Math.min(100, Math.round((100 * o.helmet) / o.persons)) : null,
  }));
  const wk = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'].map((d) => ({ dia: d, hh: 0, n: 0 }));
  for (const [k, d] of days) {
    const i = (new Date(k + 'T12:00:00Z').getUTCDay() + 6) % 7;
    if (d.act > 0) { wk[i].hh += d.hh; wk[i].n += 1; }
  }
  return { day, week: wk.map((w) => ({ dia: w.dia, hh: w.n ? Math.round(w.hh / w.n) : 0, dias: w.n })) };
}

// Visitas de un tipo de vehículo: fotos seguidas (≤ 30 min) con esa clase = una visita
function visits(photos, cfg, classes) {
  const out = [];
  let cur = null;
  for (const p of photos) {
    const has = photoStats(p, cfg).dets.some((d) => classes.includes(d.cls));
    if (has) {
      if (cur && p.ms - cur.last <= 30 * 60e3 && dayKey(p.ms) === dayKey(cur.first)) cur.last = p.ms;
      else { cur = { first: p.ms, last: p.ms }; out.push(cur); }
    }
  }
  return out;
}

export function diagnose(photos, cfg, fromKey, toKey) {
  const out = [];
  if (!photos.length) return out;
  const k = periodKpis(photos, cfg);
  const { day } = profiles(photos, cfg);
  const at = (a, b) => day.filter((s) => s.min >= a && s.min < b);
  const avg = (arr, f) => (arr.length ? arr.reduce((x, s) => x + f(s), 0) / arr.length : 0);
  const plena = avg(at(10 * 60, 12 * 60).concat(at(14 * 60, 16 * 60)), (s) => s.personas) || k.pico / 2;
  const dias = Math.max(1, k.diasActivos);

  // 1. Arranque
  if (k.inicio != null) {
    const tard = k.inicio - cfg.jIni;
    if (tard > 15) {
      const perdida = (tard / 60) * plena;
      out.push({
        key: 'arranque', level: tard > 30 ? 'alerta' : 'oportunidad', titulo: 'La obra arranca tarde',
        dato: `${fmtMin(k.inicio)} (pactado ${fmtMin(cfg.jIni)})`,
        detalle: `En promedio la actividad empieza ${hm(tard)} después del ingreso. Con ~${Math.round(plena)} personas en obra son ~${r1(perdida)} h-hombre por día, unas ${Math.round(perdida * dias)} h en el período.`,
        accion: 'Fichaje de ingreso con foto (Lisual Identity) y charla de 5 minutos a la hora exacta. Revisar el transporte del personal y que el material esté listo la tarde anterior.',
      });
    } else {
      out.push({ key: 'arranque', level: 'bien', titulo: 'Arranque puntual', dato: fmtMin(k.inicio), detalle: `La actividad empieza en hora (pactado ${fmtMin(cfg.jIni)}).`, accion: 'Mantener el control de ingreso.' });
    }
  }

  // 2. Cierre
  if (k.fin != null && cfg.jFin - k.fin > 20) {
    const ant = cfg.jFin - k.fin;
    out.push({
      key: 'cierre', level: 'oportunidad', titulo: 'Se corta antes de hora',
      dato: `${fmtMin(k.fin)} (pactado ${fmtMin(cfg.jFin)})`,
      detalle: `La última actividad aparece ${hm(ant)} antes del cierre. Son ~${r1((ant / 60) * plena)} h-hombre por día.`,
      accion: 'Planificar la limpieza y el guardado de herramientas dentro del horario y dejar asignada la tarea del día siguiente.',
    });
  }

  // 3. Corte del mediodía
  const ref = avg(at(cfg.jIni + 60, 12 * 60).concat(at(14 * 60, cfg.jFin - 60)), (s) => s.personas);
  if (ref > 0) {
    const low = at(11 * 60 + 30, 15 * 60).filter((s) => s.personas < ref * 0.55);
    if (low.length) {
      const dur = low.length * SLOT;
      const desde = low[0].min, hasta = low[low.length - 1].min + SLOT;
      out.push({
        key: 'almuerzo', level: dur > 75 ? 'oportunidad' : 'bien',
        titulo: dur > 75 ? 'El corte del mediodía se estira' : 'Corte del mediodía normal',
        dato: `~${hm(dur)} (${fmtMin(desde)}–${fmtMin(hasta)})`,
        detalle: dur > 75
          ? `Entre ${fmtMin(desde)} y ${fmtMin(hasta)} la gente en obra cae a menos de la mitad. Si el corte pactado es de 1 hora, se pierden ~${r1(((dur - 60) / 60) * ref)} h-hombre por día.`
          : 'La caída de personal al mediodía dura lo esperable.',
        accion: dur > 75 ? 'Fijar horario de almuerzo escalonado por cuadrilla y una señal clara de reinicio.' : 'Nada que hacer.',
      });
    }
  }

  // 4. Casco (dato final para el cliente)
  if (k.casco != null) {
    const worst = day.filter((s) => s.casco != null && s.personas >= 1).sort((a, b) => a.casco - b.casco)[0];
    const ok = k.casco >= 90;
    out.push({
      key: 'casco', level: ok ? 'bien' : 'alerta', titulo: ok ? 'Buen uso de casco' : 'Hay gente trabajando sin casco',
      dato: `${Math.round(k.casco)}% de las personas con casco`,
      detalle: ok ? 'El uso de casco se mantiene alto en todo el período.' : `${Math.round(100 - k.casco)}% de las personas vistas en obra no tenían casco.${worst ? ` La franja más floja es ${fmtMin(worst.min)}–${fmtMin(worst.min + SLOT)} (${worst.casco}% con casco).` : ''}`,
      accion: ok ? 'Seguir con el control de EPP.' : `Control de EPP en el ingreso y a la vuelta del almuerzo${worst ? `, y recorrida del responsable de seguridad cerca de las ${fmtMin(worst.min)}` : ''}. Usar las fotos del visor (filtro "Personas sin casco") en la charla de seguridad.`,
      cta: 'revisar',
    });
  }

  // 5. Días hábiles sin actividad
  const habiles = listDays(fromKey, toKey).filter((d) => { const w = new Date(d + 'T12:00:00Z').getUTCDay(); return w >= 1 && w <= 5 && d < dayKey(Date.now()); });
  const parados = habiles.filter((d) => { const x = k.porDia.get(d); return x && x.first == null; });
  const sinFotos = habiles.filter((d) => !k.porDia.get(d));
  if (parados.length) {
    out.push({
      key: 'parados', level: 'alerta', titulo: `${parados.length} ${parados.length === 1 ? 'día hábil' : 'días hábiles'} sin actividad`,
      dato: parados.slice(0, 5).map((d) => `${fmtDow(d)} ${fmtKey(d).slice(0, 5)}`).join(' · ') + (parados.length > 5 ? '…' : ''),
      detalle: 'Hubo fotos pero nadie trabajando en la obra esos días.',
      accion: 'Confirmar el motivo (lluvia, feriado, falta de material o de pago) y dejarlo en el parte. Si fue lluvia, tener tareas bajo techo planificadas.',
    });
  }
  if (sinFotos.length) {
    out.push({
      key: 'sinfotos', level: 'info', titulo: 'Días sin fotos de la cámara',
      dato: sinFotos.slice(0, 5).map((d) => `${fmtDow(d)} ${fmtKey(d).slice(0, 5)}`).join(' · '),
      detalle: 'La cámara no mandó fotos esos días hábiles, así que no se puede medir la obra.',
      accion: 'Revisar alimentación y conexión de la cámara (o avisar a soporte de Lisual).',
    });
  }

  // 6. Fin de semana
  const finde = [...k.porDia.entries()].filter(([d, x]) => { const w = new Date(d + 'T12:00:00Z').getUTCDay(); return (w === 0 || w === 6) && x.first != null && x.hh > 2; });
  if (finde.length) {
    out.push({
      key: 'finde', level: 'info', titulo: 'Se trabajó el fin de semana',
      dato: finde.slice(0, 4).map(([d, x]) => `${fmtDow(d)} ${fmtKey(d).slice(0, 5)} (${Math.round(x.hh)} h-h)`).join(' · '),
      detalle: 'Hubo actividad en sábado o domingo.',
      accion: 'Verificar que las horas extra estén autorizadas y que haya un responsable de seguridad en obra.',
    });
  }

  // 7. Dotación variable
  const picos = [...k.porDia.entries()].filter(([, x]) => x.first != null).map(([d, x]) => ({ d, p: x.peak }));
  if (picos.length >= 4) {
    const m = picos.reduce((a, b) => a + b.p, 0) / picos.length;
    const sd = Math.sqrt(picos.reduce((a, b) => a + (b.p - m) ** 2, 0) / picos.length);
    const lo = picos.reduce((a, b) => (b.p < a.p ? b : a)), hi2 = picos.reduce((a, b) => (b.p > a.p ? b : a));
    if (m > 0 && sd / m > 0.3) {
      out.push({
        key: 'dotacion', level: 'oportunidad', titulo: 'La dotación cambia mucho de un día a otro',
        dato: `de ${lo.p} (${fmtDow(lo.d)} ${fmtKey(lo.d).slice(0, 5)}) a ${hi2.p} personas (${fmtDow(hi2.d)} ${fmtKey(hi2.d).slice(0, 5)})`,
        detalle: `En promedio hay ${Math.round(m)} personas en el pico del día, con mucha variación.`,
        accion: 'Contrastar con el plan de trabajos: los días flojos suelen ser esperas de material o de otro gremio. Ordenar la secuencia de subcontratistas.',
      });
    }
  }

  // 8. Maquinaria quieta
  let maqVista = 0, maqActiva = 0;
  photos.forEach((p, i) => {
    const next = photos[i + 1];
    const dt = Math.min(next && dayKey(next.ms) === dayKey(p.ms) ? next.ms - p.ms : 10 * 60e3, 15 * 60e3) / 3600e3;
    const ms = photoStats(p, cfg).dets.filter((d) => isMachine(d.cls) && !['Mixer', 'Camion', 'Camion volquete'].includes(d.cls));
    if (ms.length) { maqVista += dt; if (ms.some((d) => !d.static)) maqActiva += dt; }
  });
  if (maqVista > 3) {
    const idle = 1 - maqActiva / maqVista;
    out.push({
      key: 'maquina', level: idle > 0.5 ? 'oportunidad' : 'bien', titulo: idle > 0.5 ? 'Maquinaria parada buena parte del tiempo' : 'La maquinaria se usa',
      dato: `${r1(maqActiva)} h trabajando de ${r1(maqVista)} h en obra`,
      detalle: `La máquina estuvo quieta el ${Math.round(idle * 100)}% del tiempo que se la vio.`,
      accion: idle > 0.5 ? 'Si es alquilada, ajustar los días de alquiler a los frentes listos. Coordinar para que no espere a camiones o a la cuadrilla.' : 'Mantener la coordinación de frentes.',
    });
  }

  // 9. Logística: mixers y camiones
  const mix = visits(photos, cfg, ['Mixer']);
  if (mix.length) {
    const porDia = new Map();
    mix.forEach((v) => { const d = dayKey(v.first); porDia.set(d, [...(porDia.get(d) || []), v]); });
    const top = [...porDia.entries()].sort((a, b) => b[1].length - a[1].length).slice(0, 4);
    out.push({
      key: 'hormigon', level: 'info', titulo: `Hormigón: mixers en ${porDia.size} ${porDia.size === 1 ? 'día' : 'días'}`,
      dato: top.map(([d, v]) => `${fmtDow(d)} ${fmtKey(d).slice(0, 5)} ${fmtHM(v[0].first)}–${fmtHM(v[v.length - 1].last)}`).join(' · '),
      detalle: `Se vieron ${mix.length} visitas de mixer en el período.`,
      accion: 'Cruzarlo con los remitos de la hormigonera. Programar los hormigonados en la franja de mayor dotación y con el vibrado y el curado ya asignados.',
    });
  }
  const cam = visits(photos, cfg, ['Camion', 'Camion volquete']);
  if (cam.length >= 3) {
    const horas = new Map();
    cam.forEach((v) => { const h = Math.floor(minOfDay(v.first) / 60); horas.set(h, (horas.get(h) || 0) + 1); });
    const [hTop, nTop] = [...horas.entries()].sort((a, b) => b[1] - a[1])[0];
    out.push({
      key: 'camiones', level: nTop / cam.length > 0.4 ? 'oportunidad' : 'info', titulo: `${cam.length} visitas de camiones`,
      dato: `la mayoría entre ${hTop}:00 y ${hTop + 1}:00 (${nTop})`,
      detalle: 'Entradas y salidas de camiones (volquetes, entregas) detectadas en las fotos.',
      accion: nTop / cam.length > 0.4 ? 'Escalonar las entregas para que no se junten en la misma hora y no frenen el acceso.' : 'Las entregas están repartidas en el día.',
    });
  }

  // 10. Horas productivas
  if (day.length >= 4) {
    const tot = day.reduce((a, s) => a + s.actividad, 0);
    const best = [...day].sort((a, b) => b.actividad - a.actividad)[0];
    let acc = 0, a = null, b = null;
    for (const s of day) { acc += s.actividad; if (a == null && acc >= tot * 0.1) a = s.min; if (b == null && acc >= tot * 0.9) b = s.min + SLOT; }
    out.push({
      key: 'franja', level: 'info', titulo: 'Franja más productiva',
      dato: `${fmtMin(best.min)}–${fmtMin(best.min + SLOT)}`,
      detalle: `El 80% de la actividad ocurre entre ${fmtMin(a)} y ${fmtMin(b)}.`,
      accion: 'Programar las tareas críticas (hormigonado, izajes, inspecciones) en esa franja, cuando está toda la gente.',
    });
  }

  // 11. Tendencia dentro del período
  const ds = [...k.porDia.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1)).filter(([, x]) => x.first != null);
  if (ds.length >= 6) {
    const half = Math.floor(ds.length / 2);
    const m1 = ds.slice(0, half).reduce((a, [, x]) => a + x.hh, 0) / half;
    const m2 = ds.slice(half).reduce((a, [, x]) => a + x.hh, 0) / (ds.length - half);
    const ch = m1 ? ((m2 - m1) / m1) * 100 : 0;
    if (Math.abs(ch) > 15) {
      out.push({
        key: 'tendencia', level: ch < 0 ? 'alerta' : 'bien', titulo: ch < 0 ? 'Viene bajando el ritmo' : 'Viene subiendo el ritmo',
        dato: `${ch > 0 ? '+' : ''}${Math.round(ch)}% de horas-hombre por día`,
        detalle: `Primera mitad del período: ${Math.round(m1)} h-h/día. Segunda mitad: ${Math.round(m2)} h-h/día.`,
        accion: ch < 0 ? 'Revisar con el cronograma si la baja es planificada o una demora (gremio que no vino, material, pagos).' : 'Bien. Verificar que la dotación extra tenga frente de trabajo y EPP.',
      });
    }
  }

  // 12. Proyección
  out.push({
    key: 'proyeccion', level: 'info', titulo: 'Ritmo actual',
    dato: `${Math.round(k.horasHombre / dias)} h-hombre por día trabajado`,
    detalle: `A este ritmo son ~${Math.round((k.horasHombre / dias) * 5)} h-hombre por semana de 5 días, con un pico de ${k.pico} personas.`,
    accion: 'Comparar contra las horas presupuestadas de la etapa para anticipar desvíos.',
  });

  const ord = { alerta: 0, oportunidad: 1, info: 2, bien: 3 };
  return out.sort((a, b) => ord[a.level] - ord[b.level]);
}

// Resumen listo para pegar en WhatsApp
export function whatsappSummary(name, rangeLabel, k, items) {
  const L = [];
  L.push(`*Resumen de obra · ${name}*`);
  L.push(`${rangeLabel}`);
  L.push('');
  L.push(`👷 ${Math.round(k.horasHombre)} horas-hombre · pico ${k.pico} personas`);
  L.push(`📅 ${k.diasActivos} de ${k.diasConFotos} días con actividad`);
  L.push(`⏰ Arranque ${fmtMin(k.inicio)} · cierre ${fmtMin(k.fin)}`);
  if (k.casco != null) L.push(`⛑️ ${Math.round(k.casco)}% con casco`);
  if (k.horasMaquina > 0.5) L.push(`🚜 ${k.horasMaquina.toFixed(1)} h de máquina trabajando`);
  const imp = items.filter((i) => i.level === 'alerta' || i.level === 'oportunidad').slice(0, 4);
  if (imp.length) {
    L.push('');
    L.push('*Para mejorar*');
    imp.forEach((i) => L.push(`• ${i.titulo}: ${i.dato}. → ${i.accion}`));
  }
  L.push('');
  L.push('_Datos de la cámara Lisual de la obra._');
  return L.join('\n');
}
